<template>
  <div class="activity-log-change-detail">
    <template v-if="isPublish">
      <div
        v-if="publishTargetDocFieldDiffs.length"
        class="activity-log-change-detail__section"
      >
        <div class="activity-log-change-detail__group-title">
          <SearchHighlightText text="子本設定變更" :keyword="keyword" />
        </div>
        <div
          v-for="row in publishTargetDocFieldDiffs"
          :key="row.key"
          class="activity-log-change-detail__row"
        >
          <span class="activity-log-change-detail__label">
            <SearchHighlightText :text="row.label" :keyword="keyword" />:
          </span>
          <DiffTagListCompare
            v-if="row.isPlatforms"
            :fieldKey="row.key"
            :oldValue="row.beforeIds"
            :newValue="row.afterIds"
            :getLabel="resolvePlatformLabel"
            emptyText="ALL"
            :keyword="keyword"
          />
          <DiffValueCompare
            v-else
            :oldValue="row.beforeText"
            :newValue="row.afterText"
            :keyword="keyword"
          />
        </div>
      </div>
      <div
        v-if="publishMasterWalletGroups.length"
        class="activity-log-publish-master"
      >
        <div class="activity-log-change-detail__group-title">
          <SearchHighlightText
            :text="isPublishTargetChildDoc ? '子本區塊變更' : '母本區塊變更'"
            :keyword="keyword"
          />
        </div>
        <div
          v-for="walletGroup in publishMasterWalletGroups"
          :key="walletGroup.wallet"
          class="activity-log-change-detail__wallet"
        >
          <div
            v-if="showPublishWalletHeadings && walletGroup.label"
            class="activity-log-change-detail__wallet-title"
          >
            <SearchHighlightText :text="walletGroup.label" :keyword="keyword" />
          </div>
          <div
            v-for="group in walletGroup.groups"
            :key="`${group.docId}-${group.sectionId}`"
            class="activity-log-change-detail__section"
          >
            <div
              v-if="group.heading"
              class="activity-log-change-detail__section-title"
            >
              ．<SearchHighlightText :text="group.heading" :keyword="keyword" />
            </div>
            <PublishDiffBlock
              v-for="(block, blockIdx) in group.blocks"
              :key="`${group.docId}-${group.sectionId}-${blockIdx}`"
              class="activity-log-change-detail__block"
              :block="block"
              :keyword="keyword"
              :getDocLabel="resolveDocLabel"
              :getPlatformLabel="resolvePlatformLabel"
              :resolveSortOrderLabel="(row, side) => resolveSortOrderLabelForGroup(group, row, side)"
              :getTableColumn="key => (
                group.sectionEntry?.originSectionData?.tableColumns?.[key]
                || group.sectionEntry?.sectionData?.tableColumns?.[key]
                || block.tableColumns?.[key]
                || block.beforeTableColumns?.[key]
                || sectionsByWallet?.[walletGroup.wallet]?.[group.sectionId]?.tableColumns?.[key]
              )"
            />
          </div>
        </div>
      </div>
      <div
        v-if="!isReleaseVerOnlyPublish"
        class="activity-log-change-detail__expand"
        role="button"
        tabindex="0"
        @click.stop="onViewDetails"
        @keydown.enter.prevent="onViewDetails"
      >
        View Details
      </div>
    </template>
    <template v-else>
      <div
        v-if="changeHeadline"
        class="activity-log-change-detail__headline"
      >
        [{{ changeHeadline.bracket }}]
        <SearchHighlightText
          v-if="changeHeadline.title"
          :text="changeHeadline.title"
          :keyword="keyword"
        />
        <template v-if="changeHeadline.from">
          <code
            v-if="changeHeadline.codeFromTo"
            class="ant-tag-red"
          >
            <SearchHighlightText :text="changeHeadline.from" :keyword="keyword" />
          </code>
          <SearchHighlightText
            v-else
            :text="changeHeadline.from"
            :keyword="keyword"
          />
          <span class="activity-log-change-detail__arrow">=></span>
        </template>
        <code
          v-if="changeHeadline.to && changeHeadline.codeFromTo"
          class="ant-tag-green"
        >
          <SearchHighlightText :text="changeHeadline.to" :keyword="keyword" />
        </code>
        <SearchHighlightText
          v-else-if="changeHeadline.to"
          :text="changeHeadline.to"
          :keyword="keyword"
        />
      </div>
      <div
        v-else-if="showEntryBracket"
        class="activity-log-change-detail__headline"
      >
        [{{ bracketLabel }}]
        <SearchHighlightText
          v-if="entityName"
          :text="entityName"
          :keyword="keyword"
        />
      </div>
      <div
        v-for="row in changeRows"
        :key="row.key"
        class="activity-log-change-detail__block"
      >
        <template v-if="row.kind === 'allowedIp'">
          <div class="activity-log-change-detail__headline">
            [{{ row.actionLabel }}]
            <SearchHighlightText
              :text="getFieldLabel('allowedIPs')"
              :keyword="keyword"
            />
          </div>
          <div class="activity-log-change-detail__row">
            <span class="activity-log-change-detail__label">IP:</span>
            <code
              v-if="row.mode === 'delete'"
              class="ant-tag-red"
            >
              <SearchHighlightText :text="row.ip" :keyword="keyword" />
            </code>
            <code
              v-else-if="row.mode === 'create'"
              class="ant-tag-green"
            >
              <SearchHighlightText :text="row.ip" :keyword="keyword" />
            </code>
            <span v-else>
              <SearchHighlightText :text="row.ip" :keyword="keyword" />
            </span>
          </div>
          <div
            v-if="row.showRemark"
            class="activity-log-change-detail__row"
          >
            <span class="activity-log-change-detail__label">Remark:</span>
            <DiffValueCompare
              :oldValue="row.oldRemark"
              :newValue="row.newRemark"
              :mode="row.mode"
              :keyword="keyword"
            />
          </div>
        </template>
        <template v-else>
          <div
            class="activity-log-change-detail__row"
            :class="{ 'activity-log-change-detail__row--content': row.kind === 'content' }"
          >
            <span class="activity-log-change-detail__label">
              <SearchHighlightText :text="row.label" :keyword="keyword" />:
            </span>
            <template v-if="row.kind === 'content'">
              <DiffContentViewerCompare
                :key="row.key"
                class="activity-log-change-detail__content"
                :oldValue="row.oldValue"
                :newValue="row.newValue"
                :type="row.diffType"
                :keyword="keyword"
              />
            </template>
            <template v-else-if="row.kind === 'image'">
              <div class="activity-log-change-detail__thumb-wrap">
                <ActivityLogImageThumb
                  v-if="row.oldSrc"
                  :src="row.oldSrc"
                  :alt="row.label"
                  variant="old"
                />
                <span
                  v-if="row.oldSrc && row.newSrc"
                  class="activity-log-change-detail__arrow"
                >=></span>
                <ActivityLogImageThumb
                  v-if="row.newSrc"
                  :src="row.newSrc"
                  :alt="row.label"
                  variant="new"
                />
              </div>
            </template>
            <template v-else>
              <DiffValueCompare
                :oldValue="row.oldText"
                :newValue="row.newText"
                :mode="row.mode"
                :keyword="keyword"
              />
            </template>
          </div>
        </template>
      </div>
      <div
        v-if="!changeHeadline && !changeRows.length"
        class="activity-log-change-detail__fallback"
      >
        <SearchHighlightText :text="fallbackText" :keyword="keyword" />
      </div>
    </template>
  </div>
</template>

<script>
import {
  h,
  ref,
  watch,
  computed,
} from 'vue'

import FileExcelOutlined from '@ant-design/icons-vue/lib/icons/FileExcelOutlined'

import { titleCase } from '@/utils'
import DiffContentViewerCompare from '@/components/ApiDoc/shared/DiffContentViewerCompare.vue'
import PublishDiffBlock from '@/components/ApiDoc/publish/PublishDiffBlock.vue'
import SearchHighlightText from '@/components/ApiDoc/shared/SearchHighlightText.vue'
import DiffTagListCompare from '@/components/ApiDoc/shared/DiffTagListCompare.vue'
import DiffValueCompare, { formatValue } from '@/components/ApiDoc/shared/DiffValueCompare.vue'

import {
  ACTIVITY_ACTIONS,
  getChangeBracketLabel,
} from './constants'

import {
  diffAllowedIpRows,
  formatIpWhiteListSwitchChange,
  isActivityLogChildDocTarget,
} from './utils'

import { getFieldLabel } from '../../shared/fieldLabels'

import {
  IMAGE_KEYS,
  buildImageChangeRow,
} from '../../publish/compactPublishDiffRows'

import { isRootParentSectionId } from '../../lib/constants'
import {
  buildPublishDocSectionGroups,
  buildPublishMasterSectionGroups,
  buildUpdatedDocFieldDiffsFromDocDiffs,
  expandPublishedDocsFromActivityLog,
  formatSortOrderChangeLabel,
  getPublishedDocWallet,
} from '../../publish/publishDiff'

const collectChangeSources = (entry, { isDelete } = {}) => {
  const changes = [...(entry?.detail?.changes || [])]
  const detail = entry?.detail || {}
  const hasKey = key => changes.some(c => c.key === key)

  if (detail.guideName && typeof detail.guideName === 'object' && !hasKey('guideName')) {
    const oldValue = detail.guideName.oldValue
      ?? (isDelete ? entry?.target?.entityName : undefined)
    const { newValue } = detail.guideName
    if (oldValue !== undefined || newValue !== undefined) {
      changes.push({ key: 'guideName', oldValue, newValue })
    }
  }

  if (
    isDelete
    && !changes.length
    && entry?.target?.entityName
    && entry.target.entityType !== 'doc'
    && entry.target.entityType !== 'section'
  ) {
    const nameKey = {
      guide: 'guideName',
      template: 'templateName',
      platform: 'platformName',
      client: 'clientName',
    }[entry.target.entityType] || 'name'
    changes.push({
      key: nameKey,
      oldValue: entry.target.entityName,
      newValue: undefined,
    })
  }

  return changes
}

const ActivityLogImageThumb = {
  name: 'ActivityLogImageThumb',
  props: {
    src: { type: String, default: '' },
    alt: { type: String, default: '' },
    variant: { type: String, default: 'new' },
  },
  setup(props) {
    const broken = ref(false)
    watch(() => props.src, () => { broken.value = false })
    const onError = () => { broken.value = true }

    return () => h(
      'span',
      {
        class: [
          'activity-log-change-detail__thumb',
          `activity-log-change-detail__thumb--${props.variant || 'new'}`,
        ],
      },
      props.src && !broken.value
        ? [h('img', {
          src: props.src,
          alt: props.alt,
          onError,
        })]
        : [h(FileExcelOutlined, { title: '圖片連結已失效' })],
    )
  },
}

export default {
  name: 'ActivityLogChangeDetail',
  components: {
    SearchHighlightText,
    PublishDiffBlock,
    DiffTagListCompare,
    DiffValueCompare,
    DiffContentViewerCompare,
    ActivityLogImageThumb,
  },
  props: {
    entry: { type: Object, required: true },
    keyword: { type: String, default: '' },
    docsDataByDocId: { type: Object, default: () => ({}) },
    sectionsByWallet: { type: Object, default: () => ({}) },
    entirePlatformsMap: { type: Object, default: () => ({}) },
  },
  emits: ['open-details'],
  setup(props, { emit }) {
    const isPublish = computed(() => (
      props.entry?.action === ACTIVITY_ACTIONS.PUBLISH
      || ['publish', 'publish_master'].includes(props.entry?.detail?.kind)
    ))

    const bracketLabel = computed(() => getChangeBracketLabel(props.entry?.action))

    const entityName = computed(() => (
      props.entry?.target?.entityName
      || props.entry?.target?.docShortTitle
      || props.entry?.target?.sectionTitle
      || ''
    ))

    const changeRows = computed(() => {
      const action = props.entry?.action || ''
      const isCreate = action.endsWith('_create')
        || action === ACTIVITY_ACTIONS.SECTION_DUPLICATE
        || action === ACTIVITY_ACTIONS.DOC_DUPLICATE
      const isDelete = action.endsWith('_delete')
      const changes = collectChangeSources(props.entry, { isDelete })

      return changes.flatMap((c, idx) => {
        const key = c.key || `change-${idx}`

        if (IMAGE_KEYS.has(key)) {
          const imageRow = buildImageChangeRow(key, c.oldValue, c.newValue, { isCreate, isDelete })
          return imageRow ? [imageRow] : []
        }

        if (key === 'content' && !String(c.path || '').includes('/content/content')) {
          let diffType = 'modified'
          if (isCreate || (c.oldValue === undefined && c.newValue !== undefined)) diffType = 'added'
          else if (isDelete || (c.newValue === undefined && c.oldValue !== undefined)) diffType = 'removed'
          return [{
            key,
            kind: 'content',
            label: getFieldLabel('content'),
            oldValue: c.oldValue,
            newValue: c.newValue,
            diffType,
          }]
        }

        if (key === 'allowedIPs') {
          const switchChange = formatIpWhiteListSwitchChange(c.oldValue, c.newValue)
          if (switchChange) {
            return [{
              key,
              label: getFieldLabel(c.key),
              mode: 'update',
              oldText: switchChange.oldText,
              newText: switchChange.newText,
            }]
          }

          return diffAllowedIpRows(c.oldValue, c.newValue).map(r => ({
            kind: 'allowedIp',
            key: `allowedIPs-${r.mode}-${r.ip}`,
            actionLabel: r.actionLabel,
            ip: r.ip,
            mode: r.mode,
            oldRemark: r.oldRemark,
            newRemark: r.newRemark,
            showRemark: !!(r.oldRemark || r.newRemark),
            label: 'IP',
            oldText: '',
            newText: '',
          }))
        }

        const oldText = formatValue(c.oldValue)
        const newText = formatValue(c.newValue)
        if (!oldText && !newText) return []

        let mode = 'update'
        if (isCreate || (c.oldValue === undefined && c.newValue !== undefined)) mode = 'create'
        else if (isDelete || (c.newValue === undefined && c.oldValue !== undefined)) mode = 'delete'

        return [{
          key,
          label: getFieldLabel(c.key),
          mode,
          oldText,
          newText,
        }]
      })
    })

    const showEntryBracket = computed(() => changeRows.value.some(row => row.kind !== 'allowedIp'))

    const fallbackText = computed(() => (
      props.entry?.target?.entityName
      || props.entry?.target?.sectionTitle
      || props.entry?.target?.docShortTitle
      || '—'
    ))

    const changeHeadline = computed(() => {
      const action = props.entry?.action
      const target = props.entry?.target || {}
      const detail = props.entry?.detail || {}
      const sectionTitle = target.sectionTitle || target.entityName || ''
      const docTitle = target.entityName || target.docShortTitle || ''
      const sourceDocId = target.sourceDocId || detail.sourceDocId
      if (action === ACTIVITY_ACTIONS.SECTION_DUPLICATE) {
        return {
          bracket: '複製區塊',
          from: target.sourceSectionTitle || detail.sourceSectionTitle || '',
          to: detail.newSectionTitle || sectionTitle,
        }
      }
      if (action === ACTIVITY_ACTIONS.SECTION_CREATE) {
        return { bracket: '新增區塊', from: '', to: sectionTitle }
      }
      if (action === ACTIVITY_ACTIONS.SECTION_DELETE) {
        return { bracket: '刪除區塊', from: '', to: sectionTitle }
      }
      if (action === ACTIVITY_ACTIONS.SECTION_MOVE) {
        const sourceWallet = detail.sourceWallet || String(detail.sourceDocId || '').split('#')[0]
        const destWallet = detail.targetWallet
          || String(target.docId || '').split('#')[0]
          || props.entry?.wallet
        const walletLabel = wallet => (wallet ? `${titleCase(wallet)} Wallet` : '')
        return {
          bracket: '移動區塊',
          title: sectionTitle,
          from: walletLabel(sourceWallet),
          to: walletLabel(destWallet),
          codeFromTo: true,
        }
      }
      if (action === ACTIVITY_ACTIONS.DOC_DUPLICATE) {
        return {
          bracket: '複製子本',
          from: target.sourceEntityName
            || detail.sourceEntityName
            || props.docsDataByDocId?.[sourceDocId]?.docShortTitle
            || '',
          to: docTitle,
        }
      }
      if (action === ACTIVITY_ACTIONS.DOC_CREATE) {
        return { bracket: '新增子本', from: '', to: docTitle }
      }
      if (action === ACTIVITY_ACTIONS.DOC_DELETE) {
        return { bracket: '刪除子本', from: '', to: docTitle }
      }
      return null
    })

    const publishedDocsDataByDocId = computed(() => (
      expandPublishedDocsFromActivityLog(props.entry?.detail?.publishedDocsDataByDocId)
    ))

    const isPublishTargetChildDoc = computed(() => isActivityLogChildDocTarget(props.entry))

    const publishMasterGroups = computed(() => {
      const published = publishedDocsDataByDocId.value
      const sections = props.sectionsByWallet
      if (isPublishTargetChildDoc.value) {
        const childGroups = buildPublishDocSectionGroups(
          published,
          sections,
          props.entry.target.docId,
        )
        if (childGroups.length) return childGroups
      }
      return buildPublishMasterSectionGroups(published, sections)
    })

    const PUBLISH_WALLET_ORDER = ['multi', 'single']

    const publishMasterWalletGroups = computed(() => {
      const published = publishedDocsDataByDocId.value
      const byWallet = {}
      publishMasterGroups.value.forEach((group) => {
        const wallet = getPublishedDocWallet(group.docId, published[group.docId]) || '_'
        if (!byWallet[wallet]) byWallet[wallet] = []
        byWallet[wallet].push(group)
      })
      return Object.keys(byWallet)
        .sort((a, b) => {
          const ia = PUBLISH_WALLET_ORDER.indexOf(a)
          const ib = PUBLISH_WALLET_ORDER.indexOf(b)
          if (ia === -1 && ib === -1) return a.localeCompare(b)
          if (ia === -1) return 1
          if (ib === -1) return -1
          return ia - ib
        })
        .map(wallet => ({
          wallet,
          label: wallet === '_' ? '' : `${titleCase(wallet)} Wallet`,
          groups: byWallet[wallet],
        }))
    })

    const showPublishWalletHeadings = computed(() => (
      !isPublishTargetChildDoc.value && publishMasterWalletGroups.value.length > 1
    ))

    const publishTargetDocFieldDiffs = computed(() => {
      if (!isPublishTargetChildDoc.value) return []
      const { docId } = props.entry.target
      const docEntry = publishedDocsDataByDocId.value[docId]
      if (!docEntry) return []
      if (docEntry.updatedDocFieldDiffs?.length) return docEntry.updatedDocFieldDiffs
      return buildUpdatedDocFieldDiffsFromDocDiffs(
        docEntry.docDiffs,
        docEntry.docData || {},
        props.entirePlatformsMap || {},
      )
    })

    const isReleaseVerOnlyPublish = computed(() => {
      if (publishTargetDocFieldDiffs.value.length) return false
      const sectionIds = Object.values(publishedDocsDataByDocId.value || {})
        .flatMap(entry => Object.keys(entry?.diffDataBySectionId || {}))
      return sectionIds.length > 0 && sectionIds.every(id => id === 'ReleaseVer')
    })

    const resolveDocLabel = (docId) => {
      const detail = props.entry?.detail || {}
      const published = detail.publishedDocsDataByDocId?.[docId]
      return published?.docShortTitle
        || published?.docData?.docShortTitle
        || props.docsDataByDocId?.[docId]?.docShortTitle
        || props.entry?.target?.entityName
        || docId
        || '—'
    }

    const resolvePlatformLabel = id => props.entirePlatformsMap?.[id] || id

    const resolveParentSectionForSortOrder = (wallet, parentId) => {
      if (!parentId || isRootParentSectionId(parentId)) return null
      return props.sectionsByWallet?.[wallet]?.[parentId] || null
    }

    const resolveSortOrderLabelForGroup = (group, row, side) => {
      const entry = group?.sectionEntry || {}
      const raw = side === 'old'
        ? (entry.sectionDiffData?.before || entry.originSectionData)
        : (entry.sectionDiffData?.after || entry.sectionData || entry.originSectionData)
      const section = raw && !raw.wallet
        ? {
          ...raw,
          wallet: entry.originSectionData?.wallet
            || entry.sectionData?.wallet
            || String(group?.docId || '').split('#')[0],
        }
        : raw
      return formatSortOrderChangeLabel(
        section,
        side === 'old' ? row.oldValue : row.newValue,
        group?.docId,
        resolveParentSectionForSortOrder,
      )
    }

    const onViewDetails = () => emit('open-details')

    return {
      isPublish,
      bracketLabel,
      entityName,
      showEntryBracket,
      changeRows,
      fallbackText,
      changeHeadline,
      getFieldLabel,
      publishMasterWalletGroups,
      showPublishWalletHeadings,
      publishTargetDocFieldDiffs,
      isPublishTargetChildDoc,
      isReleaseVerOnlyPublish,
      resolveDocLabel,
      resolvePlatformLabel,
      resolveSortOrderLabelForGroup,
      onViewDetails,
    }
  },
}
</script>

<style>
.activity-log-change-detail {
  line-height: 1.5;
  font-size: 13px;
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

.activity-log-change-detail__group-title {
  display: block;
  color: #aaa;
  margin-bottom: 6px;
  border-bottom: 1px solid #eee;
}

.activity-log-change-detail__section-title {
  display: inline-block;
  font-weight: bold;
  margin-bottom: 4px;
}

.activity-log-change-detail__headline {
  display: block;
  min-width: 0;
  margin-bottom: 2px;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.activity-log-change-detail__headline code {
  white-space: pre-wrap;
  word-break: break-word;
}

.activity-log-change-detail__headline > code:first-of-type {
  margin-left: 8px;
}

.activity-log-change-detail__diff {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  width: 100%;
}

.activity-log-change-detail__wallet + .activity-log-change-detail__wallet {
  margin-top: 8px;
}

.activity-log-change-detail__wallet-title {
  display: inline-block;
  color: #888;
  font-weight: bold;
  margin-bottom: 6px;
  border-bottom: 1px solid #eee;
}

.activity-log-change-detail__section + .activity-log-change-detail__section {
  margin-top: 8px;
}

.activity-log-change-detail__block + .activity-log-change-detail__block {
  margin-top: 6px;
}

.activity-log-change-detail__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 4px;
  margin-top: 2px;
  min-width: 0;
  width: 100%;
}

.activity-log-change-detail__row--content {
  flex-direction: column;
  align-items: stretch;
}

.activity-log-change-detail__content {
  min-width: 0;
  width: 100%;
}

.activity-log-change-detail .content-viewer {
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  max-height: 300px;
  background: #fff;
}

.activity-log-change-detail .content-viewer-compare .content-viewer {
  flex: 1 1 0;
  width: calc(50% - 8px);
  max-width: calc(50% - 8px);
}

.activity-log-change-detail__label {
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.65);
}

.activity-log-change-detail__object-map {
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.activity-log-change-detail__object-map code {
  display: block;
}

.activity-log-change-detail__arrow {
  margin: 0 4px;
  color: rgba(0, 0, 0, 0.45);
}

.activity-log-change-detail__edited {
  color: rgba(0, 0, 0, 0.45);
}

.activity-log-change-detail__thumb-wrap {
  display: inline-flex;
  align-items: center;
}

.activity-log-change-detail__thumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  max-width: 120px;
  max-height: 32px;
  padding: 2px;
  vertical-align: middle;
  background: #fff;
  border-radius: 2px;
}

.activity-log-change-detail__thumb img {
  max-width: 120px;
  max-height: 28px;
  object-fit: contain;
}

.activity-log-change-detail__thumb .anticon {
  width: 32px;
  font-size: 22px;
  color: #ddd;
}

.activity-log-change-detail__thumb--old {
  border: 1px solid #ffa39e;
}

.activity-log-change-detail__thumb--new {
  border: 1px solid #b7eb8f;
}

.activity-log-change-detail__fallback {
  color: rgba(0, 0, 0, 0.65);
}

.activity-log-publish-master {
  margin-bottom: 8px;
}

.activity-log-change-detail__section {
  margin-bottom: 12px;
}

.activity-log-change-detail__expand {
  display: inline-block;
  color: #1890ff;
  cursor: pointer;
}

.activity-log-change-detail__expand:hover {
  color: #40a9ff;
}

.activity-log-change-detail code.ant-tag-red,
.activity-log-change-detail code.ant-tag-green {
  margin-right: 0;
  padding: 0 4px;
  border-radius: 2px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.activity-log-change-detail code.ant-tag-red {
  color: #cf1322;
  background: #fff1f0;
  border: 1px solid #ffa39e;
}

.activity-log-change-detail code.ant-tag-green {
  color: #389e0d;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
}
</style>
